// @ts-nocheck
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from './ui/button';
import { AlertCircle } from 'lucide-react';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { getSessionUser } from '@/lib/authSession';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global Error Boundary that catches unhandled React errors
 * and sends alerts to admin via send-error-alert edge function
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🚨 [ERROR-BOUNDARY] Uncaught error:', error, errorInfo);

    // Check if this is a stale chunk/deployment error - auto-reload instead of showing error
    const isChunkError = 
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Loading CSS chunk') ||
      error.message?.includes('is not a valid JavaScript MIME type') ||
      error.message?.includes("text/html");

    if (isChunkError) {
      const hasReloaded = sessionStorage.getItem('chunk-reload-attempted');
      if (!hasReloaded) {
        console.log('[ERROR-BOUNDARY] Stale chunk detected, reloading...');
        sessionStorage.setItem('chunk-reload-attempted', 'true');
        window.location.reload();
        return; // Don't send alert for stale chunk errors
      }
    }

    // Send error alert to admin for non-chunk errors
    this.sendErrorAlert(error, errorInfo);
  }

  private async sendErrorAlert(error: Error, errorInfo: ErrorInfo) {
    try {
      // Get current user if available
      const { data: { user } } = await getSessionUser();

      await invokeEdgeFunction('send-error-alert', {
        body: {
          errorType: 'Unhandled Application Error',
          errorMessage: error.message,
          errorStack: error.stack,
          userEmail: user?.email,
          userId: user?.id,
          context: {
            componentStack: errorInfo.componentStack,
            url: window.location.href,
            userAgent: navigator.userAgent,
          },
        },
      });

      console.log('[ERROR-BOUNDARY] Error alert sent to admin');
    } catch (alertError) {
      console.error('[ERROR-BOUNDARY] Failed to send error alert:', alertError);
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full bg-card border border-destructive rounded-lg p-8 text-center">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Something went wrong
            </h1>
            <p className="text-muted-foreground mb-6">
              An unexpected error occurred. Our team has been notified and will investigate.
            </p>
            <div className="space-y-2">
              <Button onClick={this.handleReload} className="w-full">
                Reload Page
              </Button>
              <Button
                variant="outline"
                onClick={() => (window.location.href = '/')}
                className="w-full"
              >
                Go to Home
              </Button>
            </div>
            {this.state.error && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                  Technical Details
                </summary>
                <pre className="mt-2 p-3 bg-muted rounded text-xs overflow-auto max-h-40">
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}